import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {
  RawData,
  DataAdapter,
  ExpandedQueryLevel,
  QueryVariables,
  QueryLevel,
  QueryLevels,
  PaginationPageNumbers,
  QueryBuilder,
  ExpandedQueryPath,
} from '../types';

import {Plugins, Component, EvalEnv} from '@packages/systems/wfdl';
import {schema} from '@packages/systems/cms/graphql';
import {getQueryLevels} from './QueryLevel';
import {parseQueryAST} from './QueryAST';
import {expandQueryPath} from '../utils/QueryPathUtils';
import {memoize, singleMemo, weakMemo} from '@packages/utilities/memo';
import {createQueryVariables} from './QueryVariables';
import {createPluginArguments} from './PluginArguments';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  BrowserEnvironment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {pageComponentType} from '@packages/utilities/platform';
import {Qualified} from '@packages/systems/wfdl-Qualified';

type QueryString = string;
type Leaf = true;
type IntermediateQueryTree = {
  [key: string]: IntermediateQueryTreeNode;
};
type IntermediateQueryTreeNode = IntermediateQueryTree | Leaf;

type QueryBuilders = {
  adapter: DataAdapter;
  queryBuilders: QueryBuilder[];
  variables: QueryVariables;
};

const EMPTY_FOCUSED_SYMBOL_NATIVE_ID_PATH: NativeId[] = [];

export function createQueryBuildersForSsr({
  collections,
  componentMap,
  componentType,
  baseRendererNativeIdPath,
  element,
  itemSlug,
  page,
  paginationPageNumbers,
  plugins,
  publicationId,
  publishTarget,
  searchQuery,
  showSampleSearchResults,
  timezone,
  evalEnv,
  cmsLocaleId,
}: {
  collections: CollectionsType;
  componentMap: Component.ComponentMap;
  componentType: Plugins.QualifiedElement;
  baseRendererNativeIdPath: NativeId[];
  element: ExpressionElement;
  itemSlug?: string | null | undefined;
  page: PageRecordType;
  paginationPageNumbers: PaginationPageNumbers;
  plugins: Plugins.PluginsType;
  publicationId: string;
  publishTarget: string | null | undefined;
  searchQuery?: string | null | undefined;
  showSampleSearchResults?: boolean;
  timezone: string;
  evalEnv: EvalEnv.EvalEnvType;
  cmsLocaleId: string | null | undefined;
}): QueryBuilders {
  const variables = createQueryVariables({
    itemSlug,
    itemId: undefined,
    searchQuery,
    publishTarget,
  });

  const pluginArguments = createPluginArguments({
    collections,
    paginationPageNumbers,
    publicationId,
    runtimeEnvironment: WebflowEnvironments.SSR,
    showSampleSearchResults,
    timezone,
    cmsLocaleId,
  });

  const {adapter, queryLevels} = getQueryLevels(
    element,
    componentMap,
    componentType,
    plugins,
    page,
    variables,
    pluginArguments,
    evalEnv,
    baseRendererNativeIdPath,
    EMPTY_FOCUSED_SYMBOL_NATIVE_ID_PATH,
    pageComponentType
  );

  const queryBuilders =
    createSsrQueryBuildersFromQueryLevels(variables)(queryLevels);

  return {queryBuilders, variables, adapter};
}

const createSsrQueryBuildersFromQueryLevels = singleMemo(
  (queryVariables: QueryVariables) => {
    const queryVariableString = variableTypeString(queryVariables);
    return weakMemo((queryLevels: QueryLevels) => {
      return queryLevels.reduce<QueryBuilder[]>((builders, queryLevel) => {
        if (queryLevel.length > 0) {
          const queryBuilder = memoize((data: RawData) => {
            const expandedQueryLevel = expandQueryLevel(queryLevel, data);
            const queryBodyString = queryLevelToQueryString(expandedQueryLevel);
            const queryDocumentString = `query Dynamo3${queryVariableString} ${queryBodyString}`;
            return parseQueryAST(schema, queryDocumentString);
          });
          builders.push(queryBuilder);
        }
        return builders;
      }, []);
    });
  }
);

export function createQueryBuildersForClient({
  collections,
  componentMap,
  componentType,
  element,
  itemId,
  page,
  paginationPageNumbers,
  plugins,
  publishTarget,
  runtimeEnvironment,
  searchQuery,
  showSampleSearchResults,
  timezone,
  evalEnv,
  cmsLocaleId,
  baseRendererNativeIdPath,
  focusedSymbolNativeIdPath,
  rootComponentType,
}: {
  collections: CollectionsType;
  componentMap: Component.ComponentMap;
  componentType: Plugins.QualifiedElement;
  element: ExpressionElement;
  itemId?: string | null | undefined;
  page: PageRecordType;
  paginationPageNumbers: PaginationPageNumbers;
  plugins: Plugins.PluginsType;
  publishTarget: string | null | undefined;
  runtimeEnvironment: BrowserEnvironment;
  searchQuery?: string | null | undefined;
  showSampleSearchResults?: boolean;
  timezone: string;
  evalEnv: EvalEnv.EvalEnvType;
  cmsLocaleId: string | null | undefined;
  baseRendererNativeIdPath: NativeId[];
  focusedSymbolNativeIdPath: NativeId[];
  rootComponentType: Qualified<Component.ComponentName>;
}): QueryBuilders {
  const variables = createQueryVariables({
    itemId,
    itemSlug: undefined,
    searchQuery,
    publishTarget,
  });

  const pluginArguments = createPluginArguments({
    collections,
    paginationPageNumbers,
    publicationId: '',
    runtimeEnvironment,
    showSampleSearchResults,
    timezone,
    cmsLocaleId,
  });

  const {adapter, queryLevels} = getQueryLevels(
    element,
    componentMap,
    componentType,
    plugins,
    page,
    variables,
    pluginArguments,
    evalEnv,
    baseRendererNativeIdPath,
    focusedSymbolNativeIdPath,
    rootComponentType
  );

  const queryBuilder: QueryBuilder =
    createClientQueryBuilderFromQueryLevels(variables)(queryLevels);

  return {queryBuilders: [queryBuilder], variables, adapter};
}

const createClientQueryBuilderFromQueryLevels = singleMemo(
  (queryVariables: QueryVariables) => {
    const queryVariableString = variableTypeString(queryVariables);
    return weakMemo((queryLevels: QueryLevels) => {
      return memoize((mockPageItemData: RawData) => {
        const [level1, level2] = queryLevels;
        if (level1.length === 0 && level2.length === 0) {
          return null;
        }

        /*
          Fetch data for both level 1 and level 2 queries in a single query.

          Collection lists with settings bound to Dynamo data or with DYN_CONTEXT
          filters will usually have both a level 1 and a level 2 query. We need
          to fetch data for both queries in a single request to avoid chaining
          requests while still ensuring that we have all the data available
          needed to render the collection list correctly.

          Not fetching the level 1 query can result in issues such as pagination
          not working correctly in the Designer or collection list legacy style
          bindings not being applied and rendered correctly.
         */
        const queryBodyString = queryLevelToQueryString([
          ...expandQueryLevel(level1, null),
          ...expandQueryLevel(level2, mockPageItemData),
        ]);
        const queryDocumentString = `query Dynamo3${queryVariableString} ${queryBodyString}`;
        return parseQueryAST(schema, queryDocumentString);
      });
    });
  }
);

const COMMON_VARIABLES_TYPE_STRING =
  '$query: String!, $publishTarget: String!, $finalizedOrder: CommerceOrderFinalizedOrderArgs';

const variableTypeString = (variables: QueryVariables) => {
  if (variables.itemId) {
    return `($itemId: ID, ${COMMON_VARIABLES_TYPE_STRING})`;
  }
  if (variables.itemSlug) {
    return `($itemSlug: String, ${COMMON_VARIABLES_TYPE_STRING})`;
  }
  return `(${COMMON_VARIABLES_TYPE_STRING})`;
};

const expandQueryLevel = (
  queryLevel: QueryLevel,
  data: RawData
): ExpandedQueryLevel => queryLevel.map(expandQueryPath(data));

const queryLevelToQueryString = (
  queryLevel: ExpandedQueryLevel
): QueryString => {
  const intermediateQueryTree = queryLevel.reduce<IntermediateQueryTree>(
    (memo, queryPath) => {
      if (!hasQueryPath(memo, queryPath)) {
        setQueryPath(memo, queryPath);
      }
      return memo;
    },
    {}
  );
  return intermediateQueryTreeToString(intermediateQueryTree);
};

const hasQueryPath = (
  initialObj: IntermediateQueryTree,
  path: ExpandedQueryPath
): boolean => {
  let obj: IntermediateQueryTreeNode = initialObj;

  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (key === undefined || obj === true) {
      return false;
    }

    if (!obj.hasOwnProperty || !obj.hasOwnProperty(key)) {
      return false;
    }

    const nextObj: IntermediateQueryTreeNode | undefined = obj[key];
    if (!nextObj) {
      return false;
    }

    obj = nextObj;
  }
  return true;
};

const setQueryPath = (
  obj: IntermediateQueryTree,
  path: ExpandedQueryPath
): void => {
  const lastKeyIndex = path.length - 1;
  const initialKeys = path.slice(0, lastKeyIndex);
  const lastKey = path[lastKeyIndex];

  for (let i = 0; i < initialKeys.length; i++) {
    const key = initialKeys[i];
    if (key === undefined) {
      break;
    }

    if (typeof obj[key] !== 'object') {
      obj[key] = {};
    }
    obj = obj[key];
  }

  if (lastKey && typeof obj[lastKey] !== 'object') {
    obj[lastKey] = true;
  }
};

const intermediateQueryTreeToString = (
  intermediateQueryTree: IntermediateQueryTreeNode
): QueryString => {
  if (intermediateQueryTree === true) {
    // We've reached the leaf node
    return '';
  }

  const treeString = Object.keys(intermediateQueryTree)
    // NOTE: Sorting the keys here is very important for
    // memoization. Think twice before removing this line.
    .sort()
    .reduce<string[]>((memo, key) => {
      const value = intermediateQueryTree[key];
      if (value === undefined) {
        return memo;
      }
      return memo.concat(`${key} ${intermediateQueryTreeToString(value)}`);
    }, [])
    .join(' ');
  return `{${treeString}}`;
};
